import { useEffect, useState } from "react";
import { api, formatApiError, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatXOF, formatDate, METHOD_LABELS, METHOD_COLORS } from "@/lib/format";
import { Plus, Receipt, XCircle, MoneyWavy, DownloadSimple } from "@phosphor-icons/react";

const METHODS = ["cash", "orange_money", "moov_money", "wave", "bank", "check", "card"];

export default function Fees() {
  const [tab, setTab] = useState("invoices");
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [students, setStudents] = useState([]);
  const [openInvoice, setOpenInvoice] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [openFeeType, setOpenFeeType] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ student_id: "", items: [{ fee_type_id: "", description: "", amount: 0 }], due_date: "" });
  const [paymentForm, setPaymentForm] = useState({ student_id: "", invoice_id: "", amount: 0, method: "cash", reference: "", note: "" });
  const [feeTypeForm, setFeeTypeForm] = useState({ name: "", description: "" });

  const load = async () => {
    try {
      const [inv, pay, ft, st] = await Promise.all([
        api.get("/invoices"), api.get("/payments"), api.get("/fee-types"), api.get("/students"),
      ]);
      setInvoices(inv.data); setPayments(pay.data); setFeeTypes(ft.data); setStudents(st.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const totalInvoices = invoices.reduce((s, i) => s + i.total, 0);
  const totalPaid = invoices.reduce((s, i) => s + i.paid, 0);
  const totalOutstanding = invoices.reduce((s, i) => s + i.balance, 0);

  const studentById = Object.fromEntries(students.map(s => [s.id, s]));

  const addInvoiceItem = () => setInvoiceForm(f => ({ ...f, items: [...f.items, { fee_type_id: "", description: "", amount: 0 }] }));
  const setItem = (idx, key, value) => setInvoiceForm(f => {
    const items = [...f.items];
    items[idx] = { ...items[idx], [key]: value };
    return { ...f, items };
  });
  const removeItem = (idx) => setInvoiceForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const saveInvoice = async () => {
    try {
      const payload = {
        student_id: invoiceForm.student_id,
        items: invoiceForm.items.map(i => ({ ...i, amount: Number(i.amount) })).filter(i => i.amount > 0),
        due_date: invoiceForm.due_date, note: "",
      };
      if (!payload.student_id || !payload.items.length) { toast.error("Élève et au moins un article requis"); return; }
      await api.post("/invoices", payload);
      toast.success("Facture émise + écriture comptable générée");
      setOpenInvoice(false);
      setInvoiceForm({ student_id: "", items: [{ fee_type_id: "", description: "", amount: 0 }], due_date: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const savePayment = async () => {
    try {
      if (!window.confirm(`Confirmer l'encaissement de ${formatXOF(paymentForm.amount)} via ${METHOD_LABELS[paymentForm.method]} ?`)) return;
      const payload = {
        ...paymentForm, amount: Number(paymentForm.amount),
        invoice_id: paymentForm.invoice_id || null,
      };
      await api.post("/payments", payload);
      toast.success("Paiement enregistré. Reçu généré + écriture comptable.");
      setOpenPayment(false);
      setPaymentForm({ student_id: "", invoice_id: "", amount: 0, method: "cash", reference: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const cancelPayment = async (p) => {
    const reason = window.prompt(`Motif de l'annulation du reçu ${p.receipt_number} ?`);
    if (!reason) return;
    try {
      await api.post(`/payments/${p.id}/cancel`, null, { params: { reason } });
      toast.success("Paiement annulé — écriture de contrepassation créée");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveFeeType = async () => {
    try {
      await api.post("/fee-types", feeTypeForm);
      toast.success("Type de frais créé");
      setOpenFeeType(false); setFeeTypeForm({ name: "", description: "" }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const invoicesByStudent = (sid) => invoices.filter(i => i.student_id === sid && i.status !== "paid");

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Finances</div>
          <h1 className="mt-2 font-heading text-3xl font-bold tracking-tight">Frais & Paiements</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Dialog open={openFeeType} onOpenChange={setOpenFeeType}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="btn-new-feetype"><Plus size={14} className="mr-1" /> Type de frais</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau type de frais</DialogTitle></DialogHeader>
              <div className="grid gap-3">
                <div><Label>Nom</Label><Input value={feeTypeForm.name} onChange={e => setFeeTypeForm({ ...feeTypeForm, name: e.target.value })} data-testid="input-feetype-name" /></div>
                <div><Label>Description</Label><Input value={feeTypeForm.description} onChange={e => setFeeTypeForm({ ...feeTypeForm, description: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={saveFeeType} data-testid="btn-save-feetype">Créer</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={openInvoice} onOpenChange={setOpenInvoice}>
            <DialogTrigger asChild><Button variant="outline" className="rounded-full" data-testid="btn-new-invoice"><Plus size={14} className="mr-1" /> Facture</Button></DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Nouvelle facture</DialogTitle></DialogHeader>
              <div className="grid gap-4">
                <div>
                  <Label>Élève</Label>
                  <Select value={invoiceForm.student_id} onValueChange={v => setInvoiceForm({ ...invoiceForm, student_id: v })}>
                    <SelectTrigger data-testid="select-invoice-student"><SelectValue placeholder="Choisir un élève…" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} — {s.matricule}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between"><Label>Articles</Label><Button size="sm" variant="ghost" onClick={addInvoiceItem} data-testid="btn-add-item">+ Ajouter</Button></div>
                  {invoiceForm.items.map((it, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-4">
                        <Select value={it.fee_type_id} onValueChange={v => setItem(idx, "fee_type_id", v)}>
                          <SelectTrigger data-testid={`select-item-type-${idx}`}><SelectValue placeholder="Type…" /></SelectTrigger>
                          <SelectContent>{feeTypes.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <Input className="col-span-5" placeholder="Description" value={it.description} onChange={e => setItem(idx, "description", e.target.value)} data-testid={`input-item-desc-${idx}`} />
                      <Input className="col-span-2 tabular-nums text-right" type="number" placeholder="Montant" value={it.amount} onChange={e => setItem(idx, "amount", e.target.value)} data-testid={`input-item-amount-${idx}`} />
                      <Button variant="ghost" size="icon" className="col-span-1" onClick={() => removeItem(idx)}><XCircle size={16} /></Button>
                    </div>
                  ))}
                </div>
                <div><Label>Échéance</Label><Input type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm({ ...invoiceForm, due_date: e.target.value })} /></div>
                <div className="text-right font-heading text-xl font-bold tabular-nums">Total : {formatXOF(invoiceForm.items.reduce((s, i) => s + Number(i.amount || 0), 0))}</div>
              </div>
              <DialogFooter><Button onClick={saveInvoice} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-save-invoice">Émettre la facture</Button></DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog open={openPayment} onOpenChange={setOpenPayment}>
            <DialogTrigger asChild><Button className="rounded-full bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-new-payment"><MoneyWavy size={16} className="mr-1" /> Encaisser</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau paiement</DialogTitle></DialogHeader>
              <div className="grid gap-4">
                <div>
                  <Label>Élève</Label>
                  <Select value={paymentForm.student_id} onValueChange={v => setPaymentForm({ ...paymentForm, student_id: v, invoice_id: "" })}>
                    <SelectTrigger data-testid="select-payment-student"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name} — {s.matricule}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {paymentForm.student_id && invoicesByStudent(paymentForm.student_id).length > 0 && (
                  <div>
                    <Label>Facture à régler (optionnel)</Label>
                    <Select value={paymentForm.invoice_id || "none"} onValueChange={v => setPaymentForm({ ...paymentForm, invoice_id: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="select-payment-invoice"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune (paiement libre)</SelectItem>
                        {invoicesByStudent(paymentForm.student_id).map(i => (
                          <SelectItem key={i.id} value={i.id}>{i.invoice_number} — {formatXOF(i.balance)} restant</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div><Label>Montant</Label><Input type="number" value={paymentForm.amount} onChange={e => setPaymentForm({ ...paymentForm, amount: e.target.value })} data-testid="input-payment-amount" /></div>
                <div>
                  <Label>Méthode</Label>
                  <Select value={paymentForm.method} onValueChange={v => setPaymentForm({ ...paymentForm, method: v })}>
                    <SelectTrigger data-testid="select-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODS.map(m => <SelectItem key={m} value={m}>{METHOD_LABELS[m]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Référence transaction (Mobile Money, chèque…)</Label><Input value={paymentForm.reference} onChange={e => setPaymentForm({ ...paymentForm, reference: e.target.value })} data-testid="input-payment-ref" /></div>
                <div><Label>Note</Label><Textarea value={paymentForm.note} onChange={e => setPaymentForm({ ...paymentForm, note: e.target.value })} rows={2} /></div>
              </div>
              <DialogFooter><Button onClick={savePayment} className="bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="btn-save-payment">Encaisser</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Facturé</div>
          <div className="font-heading text-2xl font-bold tabular-nums mt-1">{formatXOF(totalInvoices)}</div>
        </Card>
        <Card className="p-5 border-secondary">
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Encaissé</div>
          <div className="font-heading text-2xl font-bold tabular-nums mt-1">{formatXOF(totalPaid)}</div>
        </Card>
        <Card className="p-5 border-accent">
          <div className="text-xs uppercase tracking-widest text-accent font-semibold">Créances</div>
          <div className="font-heading text-2xl font-bold tabular-nums mt-1">{formatXOF(totalOutstanding)}</div>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Factures ({invoices.length})</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Paiements ({payments.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices" className="mt-4">
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">N°</th><th className="px-4 py-3">Élève</th><th className="px-4 py-3">Total</th><th className="px-4 py-3">Payé</th><th className="px-4 py-3">Restant</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3">Émise</th>
              </tr></thead>
              <tbody>
                {invoices.map((inv, i) => {
                  const st = studentById[inv.student_id];
                  return (
                    <tr key={inv.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""}`}>
                      <td className="px-4 py-3 font-mono text-xs">{inv.invoice_number}</td>
                      <td className="px-4 py-3">{st ? `${st.first_name} ${st.last_name}` : "—"}</td>
                      <td className="px-4 py-3 tabular-nums text-right">{formatXOF(inv.total)}</td>
                      <td className="px-4 py-3 tabular-nums text-right text-secondary">{formatXOF(inv.paid)}</td>
                      <td className="px-4 py-3 tabular-nums text-right font-semibold">{formatXOF(inv.balance)}</td>
                      <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{formatDate(inv.created_at)}</td>
                    </tr>
                  );
                })}
                {!invoices.length && <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucune facture.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-4">
          <div className="bg-card border border-border rounded-lg overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Reçu</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Élève</th><th className="px-4 py-3">Méthode</th><th className="px-4 py-3">Montant</th><th className="px-4 py-3">Caissier</th><th className="px-4 py-3">Statut</th><th className="px-4 py-3"></th>
              </tr></thead>
              <tbody>
                {payments.map((p, i) => {
                  const st = studentById[p.student_id];
                  return (
                    <tr key={p.id} className={`border-b border-border last:border-0 ${i % 2 ? "bg-muted/30" : ""} ${p.status === "cancelled" ? "opacity-50" : ""}`}>
                      <td className="px-4 py-3 font-mono text-xs">{p.receipt_number}</td>
                      <td className="px-4 py-3">{formatDate(p.date)}</td>
                      <td className="px-4 py-3">{st ? `${st.first_name} ${st.last_name}` : "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${METHOD_COLORS[p.method]}`}>
                          {METHOD_LABELS[p.method]}
                        </span>
                      </td>
                      <td className="px-4 py-3 tabular-nums text-right font-semibold">{formatXOF(p.amount)}</td>
                      <td className="px-4 py-3 text-xs">{p.cashier_name}</td>
                      <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => downloadFile(`/payments/${p.id}/receipt.pdf`, `recu_${p.receipt_number}.pdf`).catch(e => toast.error(e.message))}
                            data-testid={`btn-receipt-${p.id}`}
                          >
                            <DownloadSimple size={14} className="mr-1" /> Reçu
                          </Button>
                          {p.status !== "cancelled" && (
                            <Button variant="ghost" size="sm" onClick={() => cancelPayment(p)} data-testid={`btn-cancel-${p.id}`}>
                              <XCircle size={14} className="mr-1 text-destructive" /> Annuler
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!payments.length && <tr><td colSpan={8} className="px-4 py-12 text-center text-sm text-muted-foreground">Aucun paiement.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    paid: { label: "Réglée", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
    unpaid: { label: "Impayée", cls: "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-200" },
    partial: { label: "Partielle", cls: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200" },
    confirmed: { label: "Confirmé", cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200" },
    cancelled: { label: "Annulé", cls: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200" },
  };
  const m = map[status] || { label: status, cls: "bg-muted text-foreground" };
  return <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${m.cls}`}>{m.label}</span>;
}
